/**
 * "Fill monthly from Ejar" progress, on every page (mounted once in the AppShell).
 *
 *   • The job this tab started: progress (the engine reads and saves), errors, Stop, Close.
 *   • A job started elsewhere (another user, tab or device, or this tab before a reload): who
 *     started it and when, how far - so nobody starts it twice.
 */
import React, { useEffect, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import { usePermissions } from '../hooks/usePermissions';
import { ejarFillJob } from '../services/ejarFillJob';

const REMOTE_POLL_MS = 20000;
const RUNNING = ['starting', 'running'];

const hhmm = (iso) => String(iso || '').slice(11, 16);
const who = (s) => String(s || '?').split('@')[0];

function Bar({ value, total, label }) {
    const pct = total ? Math.min(100, Math.round((value / total) * 100)) : 0;
    return (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2" role="progressbar" aria-label={label}
            aria-valuemin={0} aria-valuemax={total || 0} aria-valuenow={value}>
            <div className="h-full bg-purple-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
    );
}

export default function EjarFillStatus() {
    const { hasPermission } = usePermissions();
    const allowed = hasPermission('contracts_fill_from_ejar');
    const [s, setS] = useState(() => ejarFillJob.get());

    useEffect(() => ejarFillJob.subscribe(setS), []);
    // A job started elsewhere: ask the engine now and every 20 s (this tab's own job is followed closely)
    useEffect(() => {
        if (!allowed) return undefined;
        ejarFillJob.refreshRemote();
        const t = setInterval(() => { if (!document.hidden) ejarFillJob.refreshRemote(); }, REMOTE_POLL_MS);
        return () => clearInterval(t);
    }, [allowed]);

    const { local, remote } = s;
    const mine = local && remote && local.jobId && remote.jobId === local.jobId;
    // only a job still reading: a finished one is over for everybody
    const showRemote = allowed && remote && remote.status === 'running' && !mine && !(local && RUNNING.includes(local.status));
    if (!local && !showRemote) return null;

    // solid white: nothing of the page shows through
    const card = 'fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] shadow-lg text-sm !bg-white';

    if (local) {
        const running = RUNNING.includes(local.status);
        return (
            <Card className={card} role="status" aria-live="polite">
                <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Fill monthly from Ejar</span>
                    <div className="flex items-center gap-2">
                        {running && <Button variant="danger" size="xs" onClick={() => ejarFillJob.cancel()} disabled={local.cancel}>{local.cancel ? 'Stopping…' : 'Stop'}</Button>}
                        {!running && <Button variant="ghost" size="xs" iconOnly aria-label="Close" onClick={() => ejarFillJob.dismiss()} leadingIcon={<Icon glyph={ICONS.close} size="sm" />} />}
                    </div>
                </div>
                <Bar value={local.done} total={local.total} label="Ejar fill progress" />
                <div className="flex justify-between text-xs">
                    <span className="tabular-nums">{local.done}/{local.total} done · {local.saved} saved{local.unchanged ? ` · ${local.unchanged} unchanged` : ''}{local.skipped ? ` · ${local.skipped} not changed on Ejar` : ''}</span>
                    <span className={local.errors.length ? 'text-red-700' : 'text-gray-500'}>{local.errors.length} error(s)</span>
                </div>
                {local.current && <div className="text-xs text-gray-500 mt-1 font-mono">reading {local.current}…</div>}
                {running && <div className="text-[11px] text-gray-500 mt-1">The engine reads and saves every contract - you can close this page.</div>}
                {!running && <div className="text-xs mt-1 font-medium text-gray-700">{local.status}</div>}
                {local.errors.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-auto border-t border-gray-200 pt-1">
                        {local.errors.map((e, i) => <div key={i} className="text-[11px] text-red-700 font-mono">{e.contract}: {e.error}</div>)}
                    </div>
                )}
            </Card>
        );
    }

    // a job started elsewhere
    return (
        <Card className={card} role="status" aria-live="polite">
            <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">Fill monthly from Ejar · running</span>
                <Button variant="ghost" size="xs" iconOnly aria-label="Hide" onClick={() => ejarFillJob.hideRemote(remote.jobId)} leadingIcon={<Icon glyph={ICONS.close} size="sm" />} />
            </div>
            <div className="text-xs text-gray-700 mb-2">Started by <b>{who(remote.startedBy)}</b> at {hhmm(remote.startedAt)} - no need to start it again.</div>
            <Bar value={remote.done || 0} total={remote.total} label="Ejar fill progress (another session)" />
            <div className="flex justify-between text-xs">
                <span className="tabular-nums">{remote.done}/{remote.total} read · {remote.saved || 0} saved</span>
                <span className={remote.saveErrors || remote.errors ? 'text-red-700' : 'text-gray-500'}>{(remote.saveErrors || 0) + (remote.errors || 0)} error(s)</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">{remote.apply ? 'The engine reads and saves every contract.' : `Being saved by ${who(remote.saver)}.`}</div>
        </Card>
    );
}
